// CB:BEGIN contractor-onboarding-v2
/**
 * Unified contractor onboarding (verification) — single source of truth.
 * Uses GET/PUT /api/contractor/verification/self, presign, documents, accept-agreement, submit.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import SharedHeaderContractor from './SharedHeaderContractor';
import AgreementViewer from './AgreementViewer';
import { getApiUrl } from '../lib/getApiUrl';
import { useAuthFetch } from '../lib/useAuthFetch';
import { ServiceAreaSetup } from './ServiceAreaSetup';

const STEPS = ['Business', 'POC', 'License', 'Insurance', 'W9', 'Service Area', 'Agreements', 'Review'] as const;
const DOC_TYPES = ['LICENSE', 'INSURANCE_COI', 'WORKERS_COMP', 'WORKERS_COMP_EXEMPTION', 'W9'] as const;

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
  'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
  'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
  'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

function StateAutocomplete({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(value);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Keep query in sync when value changes externally (e.g. prefill from server)
  React.useEffect(() => { setQuery(value); }, [value]);

  const filtered = query.trim()
    ? US_STATES.filter((s) => s.toLowerCase().startsWith(query.trim().toLowerCase()))
    : US_STATES;

  const select = (state: string) => {
    setQuery(state);
    onChange(state);
    setOpen(false);
  };

  // Close on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} style={{ marginBottom: 16, position: 'relative' }}>
      <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>State *</label>
      <input
        type="text"
        value={query}
        autoComplete="new-password"
        placeholder="Start typing a state…"
        style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
      />
      {open && filtered.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e0e0e0', borderTop: 'none',
          borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          margin: 0, padding: 0, listStyle: 'none',
          maxHeight: 220, overflowY: 'auto',
        }}>
          {filtered.map((state) => (
            <li
              key={state}
              onMouseDown={() => select(state)}
              style={{ padding: '10px 14px', fontSize: 14, cursor: 'pointer', color: '#212121' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
            >
              {state}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface VerificationSelf {
  verification: Record<string, unknown> | null;
  progress: { missing: string[]; canSubmit: boolean; status: string; progressCount: number; totalRequired: number; requiredDocsPresent?: Record<string, boolean> };
  verificationStatus: string;
  documents?: Array<{ type: string; fileName?: string }>;
}

interface ContractorOnboardingProps {
  onNavigate: (screen: string) => void;
}

const ContractorOnboarding: React.FC<ContractorOnboardingProps> = ({ onNavigate }) => {
  const { user, isSignedIn } = useUser();
  const authFetch = useAuthFetch();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<VerificationSelf | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    businessName: '',
    businessAddress: '',
    serviceStates: [] as string[],
    yearsInBusiness: '' as string,
    licenseNumber: '',
    licenseState: '',
    licenseExpiresAt: '',
    licenseNotRequired: false,
    licenseNotRequiredReason: '',
    generalLiabilityExpiresAt: '',
    generalLiabilityPolicyNumber: '',
    workersCompRequired: true,
    workersCompExpiresAt: '',
    workersCompPolicyNumber: '',
    pocFirstName: '',
    pocLastName: '',
    pocEmail: '',
    pocPhone: '',
    pocPosition: '',
    legalBusinessName: '',
    dbaName: '',
    ein: '',
    taxClassification: '',
    authorizedRepresentativeName: '',
    authorizedRepresentativeTitle: '',
    authorizedRepresentativeEmail: '',
    authorizedRepresentativePhone: '',
  });
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfDownloadError, setPdfDownloadError] = useState<string | null>(null);

  const businessAddressRef = useRef<HTMLInputElement>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);
  const ignoreAddressPredictionsRef = useRef(false);

  // CB:BEGIN backend-address-autocomplete — uses /api/autocomplete (GOOGLE_API_KEY on backend), no VITE_GOOGLE_API_KEY needed
  const [addressSuggestions, setAddressSuggestions] = useState<{ description: string; place_id?: string }[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const fetchAddressSuggestions = useCallback(async (input: string) => {
    const trimmed = input.trim();
    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    try {
      const url = `${getApiUrl('/api/autocomplete')}?input=${encodeURIComponent(trimmed)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.predictions && Array.isArray(data.predictions)) {
          setAddressSuggestions(data.predictions);
          setShowAddressSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(e.target as Node)) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  // CB:END backend-address-autocomplete

  const fetchSelf = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(getApiUrl('/api/contractor/verification/self'));
      if (r.status === 404) {
        setError('Verification API not found (404). Run the canonical backend: npm run dev:backend');
        setData(null);
        setLoading(false);
        return;
      }
      if (r.status === 401) {
        setError('Please sign in again. Your session may have expired.');
        setData(null);
        setLoading(false);
        return;
      }
      if (!r.ok) throw new Error(`Failed to load (${r.status})`);
      const d = await r.json();
      setData(d);
      const v = d?.verification || {};
      setForm((prev) => ({
        ...prev,
        businessName: v.business_name || prev.businessName || '',
        businessAddress: v.business_address || prev.businessAddress || '',
        yearsInBusiness: v.yearsInBusiness != null ? String(v.yearsInBusiness) : prev.yearsInBusiness || '',
        licenseNumber: v.license_number || prev.licenseNumber || '',
        licenseState: v.licenseState || prev.licenseState || '',
        licenseExpiresAt: (v.licenseExpiresAt ? String(v.licenseExpiresAt).slice(0, 10) : '') || prev.licenseExpiresAt || '',
        licenseNotRequired: v.licenseNotRequired ?? prev.licenseNotRequired,
        licenseNotRequiredReason: v.licenseNotRequiredReason || prev.licenseNotRequiredReason || '',
        generalLiabilityExpiresAt: (v.generalLiabilityExpiresAt ? String(v.generalLiabilityExpiresAt).slice(0, 10) : '') || prev.generalLiabilityExpiresAt || '',
        generalLiabilityPolicyNumber: v.generalLiabilityPolicyNumber || prev.generalLiabilityPolicyNumber || '',
        workersCompRequired: v.workersCompRequired ?? prev.workersCompRequired,
        workersCompExpiresAt: (v.workersCompExpiresAt ? String(v.workersCompExpiresAt).slice(0, 10) : '') || prev.workersCompExpiresAt || '',
        workersCompPolicyNumber: v.workersCompPolicyNumber || prev.workersCompPolicyNumber || '',
        pocFirstName: v.pocFirstName || prev.pocFirstName || '',
        pocLastName: v.pocLastName || prev.pocLastName || '',
        pocEmail: v.pocEmail || prev.pocEmail || '',
        pocPhone: v.pocPhone || prev.pocPhone || '',
        pocPosition: v.pocPosition || prev.pocPosition || '',
        legalBusinessName: v.legalBusinessName || prev.legalBusinessName || '',
        dbaName: v.dbaName || prev.dbaName || '',
        ein: v.ein || prev.ein || '',
        taxClassification: v.taxClassification || prev.taxClassification || '',
        authorizedRepresentativeName: v.authorizedRepresentativeName || prev.authorizedRepresentativeName || '',
        authorizedRepresentativeTitle: v.authorizedRepresentativeTitle || prev.authorizedRepresentativeTitle || '',
        authorizedRepresentativeEmail: v.authorizedRepresentativeEmail || prev.authorizedRepresentativeEmail || '',
        authorizedRepresentativePhone: v.authorizedRepresentativePhone || prev.authorizedRepresentativePhone || '',
      }));
      setAgreementAccepted(!!d?.progress?.requiredDocsPresent?.AGREEMENT_SIGNED_PDF);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      const isNetwork = /fetch|network|failed to fetch/i.test(msg) || (e instanceof TypeError && msg.includes('fetch'));
      setError(isNetwork ? 'Cannot reach the API. Is the backend running? (npm run dev:backend)' : msg);
    } finally {
      setLoading(false);
    }
  }, [user?.id, authFetch]);

  useEffect(() => {
    if (isSignedIn && user?.id) fetchSelf();
  }, [isSignedIn, user?.id, fetchSelf]);

  const saveVerification = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    try {
      const r = await authFetch(getApiUrl('/api/contractor/verification/self'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: form.businessName,
          businessAddress: form.businessAddress,
          serviceStates: form.serviceStates,
          yearsInBusiness: form.yearsInBusiness ? Number(form.yearsInBusiness) : undefined,
          licenseNumber: form.licenseNumber,
          licenseState: form.licenseState,
          licenseExpiresAt: form.licenseExpiresAt || undefined,
          licenseNotRequired: form.licenseNotRequired,
          licenseNotRequiredReason: form.licenseNotRequiredReason || undefined,
          generalLiabilityExpiresAt: form.generalLiabilityExpiresAt || undefined,
          generalLiabilityPolicyNumber: form.generalLiabilityPolicyNumber || undefined,
          workersCompRequired: form.workersCompRequired,
          workersCompExpiresAt: form.workersCompExpiresAt || undefined,
          workersCompPolicyNumber: form.workersCompPolicyNumber || undefined,
          pocFirstName: form.pocFirstName || undefined,
          pocLastName: form.pocLastName || undefined,
          pocEmail: form.pocEmail || undefined,
          pocPhone: form.pocPhone || undefined,
          pocPosition: form.pocPosition || undefined,
          legalBusinessName: form.legalBusinessName || undefined,
          dbaName: form.dbaName || undefined,
          ein: form.ein || undefined,
          taxClassification: form.taxClassification || undefined,
          authorizedRepresentativeName: form.authorizedRepresentativeName || undefined,
          authorizedRepresentativeTitle: form.authorizedRepresentativeTitle || undefined,
          authorizedRepresentativeEmail: form.authorizedRepresentativeEmail || undefined,
          authorizedRepresentativePhone: form.authorizedRepresentativePhone || undefined,
        }),
      });
      if (!r.ok) throw new Error('Save failed');
      await fetchSelf();
      setShowSaveSuccess(true);
      setTimeout(() => {
        setShowSaveSuccess(false);
        if (step < STEPS.length - 1) setStep(step + 1);
      }, 1200);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (docType: string, file: File) => {
    if (!user?.id) return;
    setUploadingDoc(docType);
    setError(null);
    try {
      const presignRes = await authFetch(getApiUrl('/api/uploads/presign'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose: 'contractor_doc', docType, fileName: file.name, mimeType: file.type }),
      });
      if (!presignRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, storageKey, bucket } = await presignRes.json();
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) throw new Error('Upload failed');
      const regRes = await authFetch(getApiUrl('/api/contractor/verification/self/documents'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: docType, storageKey, bucket, fileName: file.name, mimeType: file.type, sizeBytes: file.size }),
      });
      if (!regRes.ok) throw new Error('Failed to register document');
      await fetchSelf();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleAcceptAgreement = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    try {
      const r = await authFetch(getApiUrl('/api/contractor/verification/self/accept-agreement'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error('Accept failed');
      const d = await r.json();
      setAgreementAccepted(!!d?.signedPdfStorageKey);
      await fetchSelf();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAgreement = useCallback(async () => {
    setPdfDownloadError(null);
    setDownloadingPdf(true);
    try {
      const url = getApiUrl('/api/agreements/contractor-platform/pdf');
      const r = await authFetch(url);
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || `Download failed (${r.status})`);
      }
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = 'CoreBids-Contractor-Platform-Agreement-v3.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (e) {
      setPdfDownloadError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloadingPdf(false);
    }
  }, [authFetch]);

  const handleSubmitForReview = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    try {
      const r = await authFetch(getApiUrl('/api/contractor/verification/self/submit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.missing?.length ? `Missing: ${j.missing.join(', ')}` : 'Submit failed');
      }
      setSubmitSuccess(true);
      window.dispatchEvent(new CustomEvent('contractor-readiness-refresh'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!isSignedIn) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div style={{ padding: 40, textAlign: 'center' }}>Please sign in to continue.</div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', padding: 40 }}>
          <h2>Verification submitted</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>Your verification is under review. We&apos;ll notify you once approved.</p>
          <button type="button" className="btn btn-primary" onClick={() => { try { localStorage.setItem('contractor_verification_just_submitted', '1'); } catch {} onNavigate('contractor-dashboard'); }}>Return to dashboard</button>
        </div>
      </div>
    );
  }

  if (loading && !error) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div style={{ padding: 40, maxWidth: 560, margin: '0 auto' }}>
          <h2 style={{ marginBottom: 16, color: '#111827' }}>Verification</h2>
          <div style={{ color: '#dc2626', marginBottom: 16 }}>{error}</div>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>
            From the repo root run: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>npm run dev:backend</code>
          </p>
          {import.meta.env.DEV && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
              API base: {typeof getApiUrl === 'function' ? getApiUrl('/api') : '(loading)'}
            </p>
          )}
          <button type="button" className="btn btn-primary" onClick={() => onNavigate('contractor-dashboard')}>Back to dashboard</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />
        <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }

  const progress = data.progress || { missing: [], canSubmit: false, status: 'NOT_STARTED', progressCount: 0, totalRequired: 8 };
  const docs = (data as any).documents || [];
  const hasDoc = (type: string) => docs.some((d: { type: string }) => d.type === type);
  const getDocFileName = (type: string) => {
    const doc = docs.find((d: { type: string; fileName?: string }) => d.type === type);
    return doc?.fileName || 'file';
  };

  const renderStepContent = () => {
    switch (step) {
      case 0: // Business
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Business Information</h3>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Provide your official business details as they appear on your business license or registration.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Business name *</label>
              <input type="text" className="form-input" value={form.businessName ?? ''} onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div style={{ marginBottom: 16 }} ref={addressSuggestionsRef}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Business address *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  ref={businessAddressRef}
                  className="form-input"
                  value={form.businessAddress ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({ ...f, businessAddress: v }));
                    if (ignoreAddressPredictionsRef.current) {
                      ignoreAddressPredictionsRef.current = false;
                      return;
                    }
                    fetchAddressSuggestions(v);
                  }}
                  onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                  placeholder="Start typing your address…"
                  autoComplete="new-password"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <ul
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 9999,
                      background: '#fff',
                      border: '1px solid #e0e0e0',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    {addressSuggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={async () => {
                          const placeId = (s as { place_id?: string }).place_id;
                          const fallback = (s as { description?: string }).description || '';
                          ignoreAddressPredictionsRef.current = true;
                          setShowAddressSuggestions(false);
                          setAddressSuggestions([]);
                          // Use place details to get formatted_address (includes zip)
                          if (placeId) {
                            try {
                              const detailsRes = await fetch(
                                `${getApiUrl('/api/places/details')}?place_id=${encodeURIComponent(placeId)}`,
                                { credentials: 'include' }
                              );
                              if (detailsRes.ok) {
                                const detailsData = await detailsRes.json();
                                const formatted = detailsData?.result?.formatted_address as string | undefined;
                                if (formatted) {
                                  // Strip trailing ", USA" for cleaner display
                                  setForm((f) => ({ ...f, businessAddress: formatted.replace(/, USA$/, '') }));
                                  return;
                                }
                              }
                            } catch (_) {}
                          }
                          if (fallback) setForm((f) => ({ ...f, businessAddress: fallback }));
                        }}
                        style={{ padding: '10px 14px', fontSize: 14, cursor: 'pointer', color: '#212121' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                      >
                        {(s as { description?: string }).description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
                Start typing (3+ chars) — select your address from the suggestions.
              </p>
            </div>
            {/* CB:BEGIN years-in-business-field */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Years in business</label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.yearsInBusiness ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, yearsInBusiness: e.target.value }))}
                placeholder="e.g. 5"
                style={{ width: 120, padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }}
              />
            </div>
            {/* CB:END years-in-business-field */}
            <div style={{ marginTop: 20, marginBottom: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Legal &amp; Tax Details (optional)</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Legal business name</label>
                <input type="text" value={form.legalBusinessName ?? ''} onChange={(e) => setForm((f) => ({ ...f, legalBusinessName: e.target.value }))} placeholder="As registered with state" style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>DBA / trade name</label>
                <input type="text" value={form.dbaName ?? ''} onChange={(e) => setForm((f) => ({ ...f, dbaName: e.target.value }))} placeholder="If different from legal name" style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>EIN / Tax ID</label>
                <input type="text" value={form.ein ?? ''} onChange={(e) => setForm((f) => ({ ...f, ein: e.target.value }))} placeholder="XX-XXXXXXX" style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Tax classification</label>
                <select value={form.taxClassification ?? ''} onChange={(e) => setForm((f) => ({ ...f, taxClassification: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: 'white' }}>
                  <option value="">Select…</option>
                  <option value="sole_proprietor">Sole Proprietor / Individual</option>
                  <option value="llc_single">LLC (single-member)</option>
                  <option value="llc_multi">LLC (multi-member)</option>
                  <option value="s_corp">S Corporation</option>
                  <option value="c_corp">C Corporation</option>
                  <option value="partnership">Partnership</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            {showSaveSuccess && <p style={{ color: '#059669', marginBottom: 12, fontWeight: 500 }}>✓ Information saved successfully</p>}
            <button type="button" className="btn btn-primary" disabled={saving} onClick={saveVerification} style={{ padding: '10px 20px', fontSize: 14 }}>{saving ? 'Saving…' : 'Save and continue'}</button>
          </div>
        );
      case 1: // POC — Point of Contact
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>
              Point of Contact
              <span
                title="This can be the owner, operations manager, or sales team member — at the company's discretion."
                style={{ marginLeft: 8, cursor: 'help', color: '#6b7280', fontSize: 15, fontWeight: 400 }}
              >
                ℹ
              </span>
            </h3>
            <div style={{ marginBottom: 16, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Provide contact information for the person CoreBids will reach if escalation or coordination is needed. This can be the owner, an operations manager, or a sales team member.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>First name *</label>
                <input type="text" value={form.pocFirstName} onChange={(e) => setForm((f) => ({ ...f, pocFirstName: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Last name *</label>
                <input type="text" value={form.pocLastName} onChange={(e) => setForm((f) => ({ ...f, pocLastName: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Email *</label>
              <input type="email" value={form.pocEmail} onChange={(e) => setForm((f) => ({ ...f, pocEmail: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Phone *</label>
              <input type="tel" value={form.pocPhone} onChange={(e) => setForm((f) => ({ ...f, pocPhone: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Position / title</label>
              <input type="text" value={form.pocPosition} onChange={(e) => setForm((f) => ({ ...f, pocPosition: e.target.value }))} placeholder="e.g. Owner, Operations Manager, Sales Manager" style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            {showSaveSuccess && <p style={{ color: '#059669', marginBottom: 12, fontWeight: 500 }}>✓ Saved</p>}
            <button type="button" className="btn btn-primary" disabled={saving} onClick={saveVerification} style={{ padding: '10px 20px', fontSize: 14 }}>{saving ? 'Saving…' : 'Save and continue'}</button>
          </div>
        );
      case 2: // License (was 1)
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Contractor License</h3>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Upload your contractor license or provide exemption documentation. License must be current and valid.
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>License number *</label>
              <input type="text" value={form.licenseNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, licenseNumber: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <StateAutocomplete
              value={form.licenseState ?? ''}
              onChange={(v) => setForm((f) => ({ ...f, licenseState: v }))}
            />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>License expiration date *</label>
              <input type="date" value={form.licenseExpiresAt ?? ''} onChange={(e) => setForm((f) => ({ ...f, licenseExpiresAt: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>
            <div style={{ marginBottom: 16, padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>License document *</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile('LICENSE', f); }} disabled={!!uploadingDoc} style={{ fontSize: 14 }} />
              {hasDoc('LICENSE') && <div style={{ color: '#059669', marginTop: 8, fontSize: 14, fontWeight: 500 }}>✓ Uploaded: {getDocFileName('LICENSE')}</div>}
            </div>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={saveVerification} style={{ padding: '10px 20px', fontSize: 14 }}>{saving ? 'Saving…' : 'Save and continue'}</button>
          </div>
        );
      case 3: // Insurance
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Insurance Coverage</h3>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Upload current Certificate of Insurance (COI) showing general liability coverage. Workers' compensation coverage or exemption is also required.
            </div>

            <div style={{ marginBottom: 16, padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>Certificate of Insurance (ACORD 25) *</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile('INSURANCE_COI', f); }} disabled={!!uploadingDoc} style={{ fontSize: 14 }} />
              {hasDoc('INSURANCE_COI') && <div style={{ color: '#059669', marginTop: 8, fontSize: 14, fontWeight: 500 }}>✓ Uploaded: {getDocFileName('INSURANCE_COI')}</div>}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>General liability expiration date *</label>
              <input type="date" value={form.generalLiabilityExpiresAt ?? ''} onChange={(e) => setForm((f) => ({ ...f, generalLiabilityExpiresAt: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>General liability policy number</label>
              <input type="text" placeholder="e.g. GL-123456789" value={form.generalLiabilityPolicyNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, generalLiabilityPolicyNumber: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
            </div>

            <div style={{ marginBottom: 16, padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                <input type="checkbox" checked={form.workersCompRequired} onChange={(e) => setForm((f) => ({ ...f, workersCompRequired: e.target.checked }))} />
                Workers' compensation required
              </label>

              {form.workersCompRequired ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>Workers' comp certificate *</label>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile('WORKERS_COMP', f); }} disabled={!!uploadingDoc} style={{ fontSize: 14 }} />
                    {hasDoc('WORKERS_COMP') && <div style={{ color: '#059669', marginTop: 8, fontSize: 14, fontWeight: 500 }}>✓ Uploaded: {getDocFileName('WORKERS_COMP')}</div>}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Workers' comp expiration date *</label>
                    <input type="date" value={form.workersCompExpiresAt ?? ''} onChange={(e) => setForm((f) => ({ ...f, workersCompExpiresAt: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500, color: '#374151' }}>Workers' comp policy number</label>
                    <input type="text" placeholder="e.g. WC-987654321" value={form.workersCompPolicyNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, workersCompPolicyNumber: e.target.value }))} style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6 }} />
                  </div>
                </>
              ) : (
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>Workers' comp exemption affidavit *</label>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile('WORKERS_COMP_EXEMPTION', f); }} disabled={!!uploadingDoc} style={{ fontSize: 14 }} />
                  {hasDoc('WORKERS_COMP_EXEMPTION') && <div style={{ color: '#059669', marginTop: 8, fontSize: 14, fontWeight: 500 }}>✓ Uploaded: {getDocFileName('WORKERS_COMP_EXEMPTION')}</div>}
                </div>
              )}
            </div>

            <button type="button" className="btn btn-primary" disabled={saving} onClick={saveVerification} style={{ padding: '10px 20px', fontSize: 14 }}>{saving ? 'Saving…' : 'Save and continue'}</button>
          </div>
        );
      case 4: // W9
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Tax Information (W-9)</h3>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Upload a completed IRS Form W-9 for tax reporting. This is required for all contractors to comply with federal tax regulations.
            </div>
            <div style={{ marginBottom: 16, padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>W-9 form *</label>
              <div style={{ marginBottom: 8, fontSize: 13, color: '#6b7280' }}>
                <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                  Download blank W-9 form from IRS.gov
                </a>
              </div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile('W9', f); }} disabled={!!uploadingDoc} style={{ fontSize: 14 }} />
              {hasDoc('W9') && <div style={{ color: '#059669', marginTop: 8, fontSize: 14, fontWeight: 500 }}>✓ Uploaded: {getDocFileName('W9')}</div>}
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setStep(5)} style={{ padding: '10px 20px', fontSize: 14 }}>Save and continue</button>
          </div>
        );
      case 5: // Service Area
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Service Area</h3>
            <div style={{ marginBottom: 16, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Set your base location and service radius. Only projects within this radius will be shown to you — and you'll only be able to bid on projects in your area.
            </div>
            <ServiceAreaSetup
              compact
              showSkip
              onSkip={() => setStep(6)}
              onSaved={() => {
                setTimeout(() => setStep(6), 800);
              }}
            />
          </div>
        );
      case 6: // Agreements
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Contractor Platform Agreement *</h3>
            <AgreementViewer agreementType="contractor-platform" maxHeight={380} />
            <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <button type="button" className="btn btn-secondary" disabled={downloadingPdf} onClick={handleDownloadAgreement}>
                {downloadingPdf ? 'Downloading…' : 'Download agreement'}
              </button>
              {pdfDownloadError && <span style={{ color: '#dc2626', fontSize: 14 }}>{pdfDownloadError}</span>}
              {progress.requiredDocsPresent?.AGREEMENT_SIGNED_PDF ? (
                <span style={{ color: 'green' }}>✓ Accepted (signed PDF on file)</span>
              ) : (
                <>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                    <input type="checkbox" checked={agreementAccepted} onChange={(e) => setAgreementAccepted(e.target.checked)} />
                    <span>I have read and agree to the agreement</span>
                  </label>
                  <button type="button" className="btn btn-primary" disabled={saving || !agreementAccepted} onClick={handleAcceptAgreement}>
                    {saving ? 'Processing…' : 'Accept and generate signed PDF'}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      case 7: // Review
        return (
          <div>
            <h3 style={{ marginBottom: 12 }}>Review & Submit</h3>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
              Review your information and submit for admin verification. You'll be notified via email once your application is reviewed.
            </div>

            <div style={{ marginBottom: 16, padding: '16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#374151' }}>
                Verification Progress
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: progress.canSubmit ? '#059669' : '#d97706', marginBottom: 4 }}>
                {progress.progressCount} / {progress.totalRequired}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {progress.canSubmit ? 'All required items complete' : 'Complete all required items to submit'}
              </div>
            </div>

            {progress.missing?.length > 0 && (
              <div style={{ marginBottom: 16, padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#991b1b' }}>Missing required items:</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#dc2626', fontSize: 13 }}>
                  {progress.missing.map((m, i) => <li key={i} style={{ marginBottom: 4 }}>{m}</li>)}
                </ul>
              </div>
            )}

            <button type="button" className="btn btn-primary" disabled={saving || !progress.canSubmit} onClick={handleSubmitForReview} style={{ padding: '10px 20px', fontSize: 14 }}>
              {saving ? 'Submitting…' : 'Submit for verification'}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container">
      <SharedHeaderContractor onNavigate={onNavigate} />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 32px', width: '100%', boxSizing: 'border-box' }}>
        <h2 style={{ textAlign: 'center', marginBottom: 20 }}>Contractor verification</h2>
        {data.verification?.rejectionReason && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <strong>Action required:</strong> {String((data.verification as any).rejectionReason)}
          </div>
        )}
        {/* CB:BEGIN verification-progress-bar */}
        {(() => {
          const v = data?.verification || {};
          const checks = [
            !!((v as any).business_name),
            !!((v as any).license_number || (v as any).licenseNotRequired),
            !!(hasDoc('INSURANCE_COI')),
            !!(hasDoc('W9')),
            !!(data?.progress?.requiredDocsPresent?.AGREEMENT_SIGNED_PDF),
          ];
          const done = checks.filter(Boolean).length;
          const pct = Math.round((done / checks.length) * 100);
          const labels = ['Business Info', 'License', 'Insurance', 'W-9', 'Stripe Payout'];
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Verification Progress</span>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{done}/{checks.length} complete</span>
              </div>
              <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#059669' : '#3b82f6', borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {labels.map((label, i) => (
                  <span
                    key={label}
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 12,
                      background: checks[i] ? '#d1fae5' : '#f3f4f6',
                      color: checks[i] ? '#065f46' : '#6b7280',
                      fontWeight: checks[i] ? 600 : 400,
                    }}
                  >
                    {checks[i] ? '✓ ' : ''}{label}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
        {/* CB:END verification-progress-bar */}
        <div style={{ marginBottom: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <button key={s} type="button" className={step === i ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setStep(i)} style={{ padding: '6px 12px' }}>{s}</button>
          ))}
        </div>
        {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}
        {uploadingDoc && <div style={{ color: '#6b7280', marginBottom: 8 }}>Uploading {uploadingDoc}…</div>}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 28 }}>{renderStepContent()}</div>
      </div>
    </div>
  );
};

export default ContractorOnboarding;
// CB:END contractor-onboarding-v2
